/**
 * Default Photographer Style Config
 *
 * Each option in fieldDefinitions carries a `promptFragment` — the exact text
 * injected into the final prompt via the {{_photographer_direction}} placeholder.
 * This makes the config fully self-contained: zero hardcoded prompt logic.
 */

export const defaultPhotographerConfig = {
  version: "1.0",
  name: "Clean Editorial",
  description:
    "Magazine-quality precision. Controlled lighting, sharp modern glass, showroom-perfect surfaces. The go-to for product catalogs and brand websites.",
  emoji: "📐",
  settings: {
    camera_type: "editorial",
    lens_style: "standard_35mm",
    lens_effects: "clean",
    camera_framing: "eye_level_wide",
    focus_balance: "balanced",
    surface_realism: "pristine",
    atmosphere: "none",
  },
  fieldDefinitions: [
    {
      key: "camera_type",
      label: "Camera Type",
      options: [
        {
          key: "documentary",
          label: "Documentary",
          description: "Handheld feel, slight asymmetry, natural tilt — feels captured, not staged",
          promptFragment: "Documentary photography style — handheld feel, slight asymmetry, natural tilt, feels captured not staged. Lighting should feel slightly imperfect with soft inconsistencies in shadow edges and subtle cool blue temperature shift in shadows",
        },
        {
          key: "editorial",
          label: "Editorial",
          description: "Composed but natural — magazine-quality with intentional framing",
          promptFragment: "Editorial photography style — composed but natural, magazine-quality with intentional framing and precise but not sterile lighting",
        },
        {
          key: "architectural",
          label: "Architectural",
          description: "Precise verticals, symmetry, tilt-shift correction — clean and technical",
          promptFragment: "Architectural photography style — precise verticals, symmetry, tilt-shift correction, clean and technical with even controlled lighting",
        },
        {
          key: "lifestyle",
          label: "Lifestyle",
          description: "Warm, intimate, slightly over-exposed highlights — aspirational living",
          promptFragment: "Lifestyle photography style — warm, intimate, slightly over-exposed highlights, aspirational living with soft natural light wrapping around subjects",
        },
      ],
    },
    {
      key: "lens_style",
      label: "Lens & Focal Length",
      options: [
        {
          key: "wide_24mm",
          label: "Wide (24mm)",
          description: "Expansive room view, slight barrel distortion at edges, dramatic depth",
          promptFragment: "24mm wide-angle lens — expansive room view, slight barrel distortion at edges, dramatic depth separation between foreground and background",
        },
        {
          key: "standard_35mm",
          label: "Standard (35mm)",
          description: "Natural perspective, minimal distortion — the workhorse architectural lens",
          promptFragment: "35mm standard lens — natural perspective, minimal distortion, the workhorse architectural lens with honest spatial rendering",
        },
        {
          key: "normal_50mm",
          label: "Normal (50mm)",
          description: "Human-eye perspective, shallow DOF potential, intimate feel",
          promptFragment: "50mm normal lens — human-eye perspective, shallow depth-of-field potential, intimate and natural feel",
        },
        {
          key: "tele_85mm",
          label: "Telephoto (85mm)",
          description: "Compressed perspective, creamy bokeh, isolates the subject",
          promptFragment: "85mm telephoto lens — compressed perspective, creamy bokeh in out-of-focus areas, isolates the subject from the background",
        },
      ],
    },
    {
      key: "lens_effects",
      label: "Lens Character",
      options: [
        {
          key: "clean",
          label: "Clean / Clinical",
          description: "Sharp edge-to-edge, no aberration — modern high-end glass",
          promptFragment: "Clean modern glass rendering — sharp edge-to-edge, no chromatic aberration, no vignetting, clinical precision",
        },
        {
          key: "natural",
          label: "Natural Imperfections",
          description: "Subtle chromatic aberration at edges, gentle vignetting, realistic DOF falloff",
          promptFragment: "Natural lens characteristics — subtle chromatic aberration at edges, realistic depth-of-field transition, slight vignetting toward corners, gentle focus falloff",
        },
        {
          key: "vintage",
          label: "Vintage Character",
          description: "Stronger vignetting, soft corners, warm flare, character-rich rendering",
          promptFragment: "Vintage lens character — stronger vignetting, soft corners, warm flare on highlights, character-rich rendering with slight glow",
        },
        {
          key: "anamorphic",
          label: "Anamorphic / Cinematic",
          description: "Oval bokeh, horizontal flare streaks, cinematic widescreen feel",
          promptFragment: "Anamorphic cinematic lens — oval bokeh shapes, horizontal flare streaks on light sources, cinematic widescreen depth feel",
        },
      ],
    },
    {
      key: "camera_framing",
      label: "Camera Framing",
      options: [
        {
          key: "wide",
          label: "Wide",
          description: "Full room visible, establishes the complete environment and context",
          promptFragment: "Wide-angle framing showing the full room",
        },
        {
          key: "eye_level_wide",
          label: "Eye Level",
          description: "Natural standing perspective, most relatable and immersive angle",
          promptFragment: "Eye-level natural standing perspective",
        },
        {
          key: "detail",
          label: "Detail",
          description: "Close-up focus on the product and its immediate surroundings",
          promptFragment: "Close-up detail framing on the product and immediate surroundings",
        },
      ],
    },
    {
      key: "focus_balance",
      label: "Focus Balance",
      options: [
        {
          key: "environment_first",
          label: "Environment First",
          description: "The room is the star — product blends naturally into the environment",
          promptFragment: "The room is the star and the product blends naturally into the environment",
        },
        {
          key: "balanced",
          label: "Balanced",
          description: "Equal weight between the space and the product — both clearly visible",
          promptFragment: "Balanced composition with equal visual weight between space and product",
        },
        {
          key: "subject_forward",
          label: "Subject Forward",
          description: "Product is the clear focal point — room supports but doesn't compete",
          promptFragment: "The product is the clear focal point, the room supports but doesn't compete",
        },
      ],
    },
    {
      key: "surface_realism",
      label: "Surface & Texture",
      options: [
        {
          key: "pristine",
          label: "Pristine",
          description: "Showroom perfect — no scuffs, no dust, immaculate surfaces",
          promptFragment: "Showroom-perfect surfaces — no scuffs, no dust, immaculate finishes throughout",
        },
        {
          key: "subtle_wear",
          label: "Subtle Wear",
          description: "Faint scuffs on skirting boards, minor wall marks, slight dust — believably real",
          promptFragment: "Surfaces show subtle wear — faint scuffs on skirting boards, minor marks on walls, slight dust on surfaces, lived-in imperfections. Textures show natural randomness — wood grain variation between planks, fabric with natural creases",
        },
        {
          key: "lived_in",
          label: "Lived-in Texture",
          description: "Wood grain variation, fabric creases, no repeating patterns — tactile authenticity",
          promptFragment: "Fully lived-in texture — wood grain variation between planks, fabric with natural creases and uneven folds, no repeating tile patterns, visible fingerprints on glass, slight dust accumulation in corners",
        },
        {
          key: "weathered",
          label: "Weathered",
          description: "Visible aging, patina on metals, worn wood edges — character and history",
          promptFragment: "Weathered and aged surfaces — visible patina on metals, worn wood edges, paint showing through layers, character and history in every material",
        },
      ],
    },
    {
      key: "atmosphere",
      label: "Atmospheric Elements",
      options: [
        {
          key: "none",
          label: "None",
          description: "Clean air, no particles or haze",
          promptFragment: "Clean atmosphere — no visible particles, haze, or atmospheric effects",
        },
        {
          key: "dust_motes",
          label: "Dust in Light",
          description: "Subtle dust particles floating in light beams",
          promptFragment: "Subtle dust particles floating in light beams, visible in shafts of sunlight, adding depth and realism to the air",
        },
        {
          key: "seasonal",
          label: "Seasonal Auto",
          description: "Contextual — condensation in winter, warm haze in summer, pollen in spring",
          promptFragment: "Seasonal atmospheric elements — condensation on cold windows in winter, warm golden haze in summer, pollen motes in spring light, fog wisps in autumn",
        },
        {
          key: "steam_warmth",
          label: "Steam / Warmth",
          description: "Steam from a cup, warm air shimmer, visible breath in cold scenes",
          promptFragment: "Warm atmospheric elements — steam rising from a coffee cup, warm air shimmer near heat sources, visible breath in cold scenes, condensation on surfaces",
        },
      ],
    },
  ],
};
